import { AppShell, Box } from '@mantine/core'
import { Await, useLocation, useMatches } from '@remix-run/react'
import type { UIMatch } from '@remix-run/react/dist/components'
import type { AdminSessionInfo, UserSessionInfo } from '~/authenticator.server'
import { AdminHeader } from '~/components/admin-header'
import { AdminNavbar } from '~/components/admin-nav-bar'
import { Breadcrumbs } from '~/components/breadcrumbs'
import { Footer } from '~/components/footer'
import { Header } from '~/components/header'

type RouteHandle = {
  breadcrumb?: (props?: UIMatch) => Promise<
    {
      id: string
      title: string
      href?: string
    }[]
  >
}

export const AppLayout = ({
  userInfo,
  adminUserInfo,
  children,
}: {
  userInfo: UserSessionInfo | null
  adminUserInfo: AdminSessionInfo | null
  children: React.ReactNode
}) => {
  const location = useLocation()
  const isAdminRoute = location.pathname.startsWith('/admin')
  const isAdminLoginRoute = location.pathname.startsWith('/admin/login')

  // MEMO: useMatchesが任意の型の指定ができないため、型アサーションをしている。
  const matches = useMatches() as UIMatch<unknown, RouteHandle | undefined>[]
  const current = matches.at(-1)

  const breadCrumbItemsPromise = current?.handle?.breadcrumb?.(current) ?? []
  const NAVBAR_WIDTH = 250
  const hasNavbar = isAdminRoute && !isAdminLoginRoute

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{
        width: hasNavbar ? NAVBAR_WIDTH : 0,
        breakpoint: 'sm',
      }}
    >
      <AppShell.Header>
        {isAdminRoute ? (
          <AdminHeader adminUserInfo={adminUserInfo} />
        ) : (
          <Header userInfo={userInfo} />
        )}
      </AppShell.Header>

      {hasNavbar ? (
        <AppShell.Navbar p='xs'>
          <AdminNavbar />
        </AppShell.Navbar>
      ) : null}

      <AppShell.Main
        px={0} // full widthにして、各routeをContainerで包む（containerのProps次第でFullWidthも可能）
        pl={hasNavbar ? NAVBAR_WIDTH : 0} // navbarがある場合は、その分左にpaddingを入れる
        style={{
          minHeight: 'unset', // unsetしないと、デフォルトでvh 100%になっちゃって、短いページの場合footerが隠れてしまう
        }}
      >
        <Await resolve={breadCrumbItemsPromise}>
          {(breadCrumbItems) =>
            breadCrumbItems.length > 0 ? (
              <>
                {/* PC */}
                <Box py='sm' pl='xl' visibleFrom='sm'>
                  <Breadcrumbs items={breadCrumbItems} />
                </Box>

                {/* SP */}
                <Box py='sm' pl='lg' hiddenFrom='sm'>
                  <Breadcrumbs items={breadCrumbItems} />
                </Box>
              </>
            ) : null
          }
        </Await>
        {children}
      </AppShell.Main>

      {isAdminRoute ? null : (
        <AppShell.Footer style={{ position: 'unset' }} mt={120}>
          <Footer />
        </AppShell.Footer>
      )}
    </AppShell>
  )
}
